import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faListCheck,
  faSync,
  faArrowUpRightFromSquare,
  faExchangeAlt,
  faSearch,
  faTimes,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import MilestoneService from '@frontend/AppSettings/services/MilestoneService';
import WorkflowTemplateService from '@frontend/AppSettings/services/WorkflowTemplateService';
import type { TemplateListItem } from '@frontend/types/workflows';
import './MatterMilestones.css';

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  try {
    // Treat calendar-only strings (YYYY-MM-DD) as local dates; `new Date(str)`
    // parses them as UTC midnight, which renders as the previous day in
    // negative UTC offsets.
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    const date = dateOnly
      ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
      : new Date(value);
    return date.toLocaleDateString();
  } catch {
    return value;
  }
};

// Normalize a stored planned date into the YYYY-MM-DD form an <input type="date"> needs.
const toDateInputValue = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return typeof value === 'string' ? value.slice(0, 10) : '';
  }
  return date.toISOString().split('T')[0];
};

const MatterMilestones: React.FC = () => {
  const { matterId } = useParams();
  const queryClient = useQueryClient();
  const [milestones, setMilestones] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState<Record<string, boolean>>({});
  const [editingDateId, setEditingDateId] = useState<string | null>(null);
  const [dateDraft, setDateDraft] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [anchorDates, setAnchorDates] = useState<Record<string, string>>({});
  const [activeTemplateName, setActiveTemplateName] = useState<string | null>(null);

  // Change Workflow state
  const [showChangeWorkflow, setShowChangeWorkflow] = useState(false);
  const [templates, setTemplates] = useState<TemplateListItem[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [newAnchorDate, setNewAnchorDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [replacing, setReplacing] = useState(false);
  const [replaceError, setReplaceError] = useState<string | null>(null);

  const orderedMilestones = useMemo(() => {
    const getOrder = (m: any) => {
      if (m.milestoneOrder != null) return m.milestoneOrder;
      if (m.stepOrder != null) return m.stepOrder;
      if (m.order != null) return m.order;
      const nameNum = parseInt((m.name || m.stepName || '').replace(/\D+/g, ''), 10);
      return Number.isNaN(nameNum) ? Number.MAX_SAFE_INTEGER : nameNum;
    };
    return [...milestones].sort((a, b) => {
      const orderA = getOrder(a);
      const orderB = getOrder(b);
      if (orderA !== orderB) return orderA - orderB;
      const dateA = a.plannedDate || a.planned_date || '';
      const dateB = b.plannedDate || b.planned_date || '';
      if (dateA && dateB) return dateA.localeCompare(dateB);
      return 0;
    });
  }, [milestones]);

  const currentStepIndex = useMemo(() => {
    if (!orderedMilestones.length) return -1;
    const idx = orderedMilestones.findIndex((m) => (m.status || '').toLowerCase() !== 'completed');
    return idx === -1 ? orderedMilestones.length - 1 : idx;
  }, [orderedMilestones]);

  const progress = useMemo(() => {
    if (!orderedMilestones.length) return 0;
    if (orderedMilestones.length === 1) return 100;
    return Math.round((currentStepIndex / (orderedMilestones.length - 1)) * 100);
  }, [currentStepIndex, orderedMilestones]);

  const completedCount = useMemo(
    () =>
      orderedMilestones.filter(
        (m) => (m.status || '').toLowerCase() === 'completed'
      ).length,
    [orderedMilestones]
  );

  const fetchMilestones = async () => {
    if (!matterId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await MilestoneService.getProjectMilestones(matterId, { limit: 200, page: 1 });
      const list = response?.data?.milestones || [];
      const anchors =
        (response as any)?.data?.anchors ||
        (response as any)?.data?.projectAnchorDates ||
        {};
      setAnchorDates(anchors || {});
      setMilestones(list as any[]);
      setActiveTemplateName((response as any)?.data?.activeTemplateName ?? null);
    } catch (err) {
      console.error('Error fetching milestones', err);
      setError('Unable to load milestones right now.');
      setMilestones([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMilestones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId]);

  const handleStatusChange = async (milestoneId: string, status: string) => {
    if (!matterId || !milestoneId) return;
    setIsUpdating((prev) => ({ ...prev, [milestoneId]: true }));
    try {
      await MilestoneService.updateMilestone(matterId, milestoneId, { status } as any);
      setMilestones((prev) =>
        prev.map((m) => (m.milestoneId === milestoneId ? { ...m, status } : m))
      );
    } catch (err) {
      console.error('Error updating milestone status', err);
    } finally {
      setIsUpdating((prev) => ({ ...prev, [milestoneId]: false }));
    }
  };

  const startDateEdit = (milestoneId: string, current?: string | null) => {
    setEditingDateId(milestoneId);
    setDateDraft(toDateInputValue(current));
  };

  const cancelDateEdit = () => {
    setEditingDateId(null);
    setDateDraft('');
  };

  const handlePlannedDateChange = async (milestoneId: string) => {
    if (!matterId || !milestoneId) {
      cancelDateEdit();
      return;
    }
    const target = milestones.find((m) => m.milestoneId === milestoneId);
    const currentValue = toDateInputValue(target?.plannedDate || target?.planned_date);
    if (!dateDraft || dateDraft === currentValue) {
      cancelDateEdit();
      return;
    }
    setIsUpdating((prev) => ({ ...prev, [milestoneId]: true }));
    setError(null);
    try {
      await MilestoneService.updateMilestone(matterId, milestoneId, {
        plannedDate: dateDraft,
      });
      setMilestones((prev) =>
        prev.map((m) =>
          m.milestoneId === milestoneId ? { ...m, plannedDate: dateDraft } : m
        )
      );
      // Keep the top-bar / info-card due date (derived from milestones) in sync.
      queryClient.invalidateQueries({ queryKey: ['projectDueDate', matterId] });
      cancelDateEdit();
    } catch (err) {
      console.error('Error updating milestone planned date', err);
      setError('Unable to update the planned date. Please try again.');
    } finally {
      setIsUpdating((prev) => ({ ...prev, [milestoneId]: false }));
    }
  };

  const openChangeWorkflow = useCallback(async () => {
    setShowChangeWorkflow(true);
    setSelectedTemplateId(null);
    setReplaceError(null);
    setTemplateSearch('');
    setTemplatesLoading(true);
    try {
      const res = await WorkflowTemplateService.listWorkflowTemplates({ limit: 200 });
      setTemplates(res?.data?.templates ?? []);
    } catch {
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  const filteredTemplates = useMemo(() => {
    if (!templateSearch.trim()) return templates;
    const q = templateSearch.toLowerCase();
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.category ?? '').toLowerCase().includes(q) ||
        (t.description ?? '').toLowerCase().includes(q)
    );
  }, [templates, templateSearch]);

  const handleReplaceWorkflow = async () => {
    if (!matterId || !selectedTemplateId || !newAnchorDate) return;
    setReplacing(true);
    setReplaceError(null);
    try {
      await MilestoneService.replaceProjectWorkflow(matterId, {
        templateId: selectedTemplateId,
        anchorDate: newAnchorDate,
      });
      queryClient.invalidateQueries({ queryKey: ['projectDueDate', matterId] });
      setShowChangeWorkflow(false);
      await fetchMilestones();
    } catch (err: any) {
      console.error('Error replacing workflow', err);
      setReplaceError(err?.message || 'Failed to replace workflow. Please try again.');
    } finally {
      setReplacing(false);
    }
  };

  const currentLabel =
    currentStepIndex >= 0 && orderedMilestones[currentStepIndex]
      ? orderedMilestones[currentStepIndex].name ||
        orderedMilestones[currentStepIndex].stepName ||
        `Step ${currentStepIndex + 1}`
      : 'No steps';

  const statusOptions = useMemo(() => {
    // Allowed statuses per contract, plus any returned values to avoid rendering blanks
    const base = [
      { value: 'planned', label: 'Planned' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'blocked', label: 'Blocked' },
      { value: 'completed', label: 'Completed' },
      { value: 'overdue', label: 'Overdue' },
    ];
    const seen = new Set(base.map((o) => o.value));
    orderedMilestones.forEach((m) => {
      const val = (m.status || '').toLowerCase();
      if (val && !seen.has(val)) {
        base.push({ value: val, label: val.replace(/_/g, ' ') });
        seen.add(val);
      }
    });
    return base;
  }, [orderedMilestones]);

  return (
    <div className="matter-milestones">
      <div className="mm-summary-card">
        <div className="mm-summary-header">
          <div>
            <div className="mm-summary-title">
              <FontAwesomeIcon icon={faListCheck} /> Milestones
            </div>
            <div className="mm-summary-subtitle">
              {activeTemplateName && (
                <span className="mm-summary-workflow-name">{activeTemplateName}</span>
              )}
              {activeTemplateName && <span className="mm-summary-dot">•</span>}
              {orderedMilestones.length} steps • {progress}% complete
            </div>
          </div>
          <div className="mm-summary-actions">
            <button
              className="mm-icon-btn"
              onClick={openChangeWorkflow}
              aria-label="Change workflow"
              title="Change workflow"
            >
              <FontAwesomeIcon icon={faExchangeAlt} />
            </button>
            <button
              className="mm-icon-btn"
              onClick={fetchMilestones}
              aria-label="Refresh milestones"
              disabled={isLoading}
            >
              <FontAwesomeIcon icon={faSync} spin={isLoading} />
            </button>
            <button
              className="mm-icon-btn"
              onClick={() => setExpanded(true)}
              aria-label="Open milestone details"
            >
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </button>
          </div>
        </div>

        <div className="mm-progress">
          <div className="mm-progress-top">
            <span className="mm-progress-label">{currentLabel}</span>
            <span className="mm-progress-value">{progress}%</span>
          </div>
          <div className="mm-progress-bar">
            <div className="mm-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="mm-progress-meta">
            <span>
              Step {currentStepIndex + 1 || 0}/{orderedMilestones.length || 0}
            </span>
            <span>{completedCount} completed</span>
          </div>
        </div>

        {anchorDates && Object.keys(anchorDates).length > 0 && (
          <div className="mm-anchor-row">
            {Object.entries(anchorDates)
              .slice(0, 3)
              .map(([k, v]) => (
                <span key={k} className="mm-anchor-chip">
                  <strong>{k}:</strong> {formatDate(v as string)}
                </span>
              ))}
          </div>
        )}

        <div className="mm-steps-preview">
          <div className="mm-steps-preview-title">Steps</div>
          {orderedMilestones.length === 0 && <div className="mm-empty">No milestones yet.</div>}
          <div className="mm-steps-preview-list">
            {orderedMilestones.map((m) => {
              const rawStatus = (m.status || '').toLowerCase();
              const statusClass = rawStatus.replace(/\s+/g, '_') || 'planned';
              const statusLabel = (rawStatus || 'planned').replace(/_/g, ' ');
              const name = m.name || m.stepName || m.title || 'Milestone';
              const planned = m.plannedDate || m.planned_date;
              return (
                <div className="mm-step-row" key={m.milestoneId || name}>
                  <div className="mm-step-main">
                    <div className="mm-step-name">{name}</div>
                    <div className="mm-step-meta">
                      {planned ? `Planned ${formatDate(planned)}` : 'No date'}
                    </div>
                  </div>
                  <span className={`mm-status-chip status-${statusClass}`}>
                    {statusLabel}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="mm-drawer">
          <div className="mm-drawer__backdrop" onClick={() => setExpanded(false)} />
          <div className="mm-drawer__panel">
            <div className="mm-panel__header">
              <div className="mm-panel__title">Milestones</div>
              <div className="mm-panel__actions">
                <span className="mm-smalltext">
                  {orderedMilestones.length} total • {progress}% complete
                </span>
                {anchorDates && Object.keys(anchorDates).length > 0 && (
                  <div className="mm-anchors-inline">
                    {Object.entries(anchorDates).map(([k, v]) => (
                      <span key={k} className="mm-anchor-chip">
                        <strong>{k}:</strong> {formatDate(v as string)}
                      </span>
                    ))}
                  </div>
                )}
                <button className="mm-refresh" onClick={fetchMilestones}>
                  <FontAwesomeIcon icon={faSync} spin={isLoading} /> Refresh
                </button>
                <button className="mm-refresh" onClick={() => setExpanded(false)}>
                  Close
                </button>
              </div>
            </div>

            {anchorDates && Object.keys(anchorDates).length > 0 && (
              <div className="mm-anchors">
                {Object.entries(anchorDates).map(([k, v]) => (
                  <span key={k} className="mm-anchor-chip">
                    <strong>{k}:</strong> {formatDate(v as string)}
                  </span>
                ))}
              </div>
            )}
            {error && <div className="mm-error">{error}</div>}
            {!error && !isLoading && orderedMilestones.length === 0 && (
              <div className="mm-empty">No milestones yet.</div>
            )}

            <div className="mm-list">
              {orderedMilestones.map((m) => {
                const planned = m.plannedDate || m.planned_date;
                const actual = m.actualDate || m.actual_date;
                const offsetType = m.offsetType || m.offset_type;
                const offsetDays = m.offsetDays ?? m.offset_days;
                const durationDays = m.durationDays ?? m.duration_days;
                const name = m.name || m.stepName || m.title || m.category || 'Milestone';
                const status = (m.status || '').toLowerCase();
                return (
                  <div className="mm-card" key={m.milestoneId}>
                    <div className="mm-card__row">
                      <div className="mm-card__main">
                        <div className="mm-card__name">{name}</div>
                        <div className="mm-card__meta">
                          Step {m.milestoneOrder ?? m.stepOrder ?? m.order ?? '-'}
                          {m.category ? ` · ${m.category}` : ''}
                          {m.discipline ? ` · ${m.discipline}` : ''}
                        </div>
                      </div>
                      <div className="mm-card__actions">
                        <select
                          className={`mm-status-select status-${status}`}
                          value={
                            statusOptions.some((o) => o.value === status)
                              ? status
                              : statusOptions[0]?.value || ''
                          }
                          disabled={!!isUpdating[m.milestoneId]}
                          onChange={(e) => handleStatusChange(m.milestoneId, e.target.value)}
                        >
                          {statusOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mm-card__details">
                      <div className="mm-detail-row">
                        {editingDateId === m.milestoneId ? (
                          <span className="mm-date-edit">
                            <strong>Planned:</strong>{' '}
                            <input
                              type="date"
                              className="mm-date-input"
                              value={dateDraft}
                              autoFocus
                              disabled={!!isUpdating[m.milestoneId]}
                              onChange={(e) => setDateDraft(e.target.value)}
                              onBlur={() => handlePlannedDateChange(m.milestoneId)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  void handlePlannedDateChange(m.milestoneId);
                                } else if (e.key === 'Escape') {
                                  cancelDateEdit();
                                }
                              }}
                            />
                          </span>
                        ) : (
                          <span
                            className="mm-date-editable"
                            role="button"
                            tabIndex={0}
                            title="Click to edit planned date"
                            onClick={() => startDateEdit(m.milestoneId, planned)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                startDateEdit(m.milestoneId, planned);
                              }
                            }}
                          >
                            <strong>Planned:</strong> {formatDate(planned)}
                          </span>
                        )}
                        <span>
                          <strong>Actual:</strong> {formatDate(actual)}
                        </span>
                        {offsetType && (
                          <span>
                            <strong>Offset:</strong> {offsetDays ?? 0} ({offsetType})
                          </span>
                        )}
                        {durationDays != null && (
                          <span>
                            <strong>Duration:</strong> {durationDays} day(s)
                          </span>
                        )}
                      </div>
                      {m.metadata && Object.keys(m.metadata).length > 0 && (
                        <div className="mm-meta-grid">
                          {Object.entries(m.metadata).map(([k, v]) => (
                            <div key={k} className="mm-meta-chip">
                              <strong>{k}:</strong> {String(v)}
                            </div>
                          ))}
                        </div>
                      )}
                      {m.warnings && m.warnings.length > 0 && (
                        <div className="mm-warnings">
                          {m.warnings.map((w: string, idx: number) => (
                            <span key={idx} className="mm-warning-chip">
                              {w}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {showChangeWorkflow && (
        <div className="mm-drawer">
          <div className="mm-drawer__backdrop" onClick={() => !replacing && setShowChangeWorkflow(false)} />
          <div className="mm-drawer__panel mm-change-workflow-panel">
            <div className="mm-panel__header">
              <div className="mm-panel__title">Change Workflow</div>
              <button
                className="mm-refresh"
                onClick={() => setShowChangeWorkflow(false)}
                disabled={replacing}
              >
                <FontAwesomeIcon icon={faTimes} /> Close
              </button>
            </div>

            <div className="mm-cw-search">
              <FontAwesomeIcon icon={faSearch} className="mm-cw-search-icon" />
              <input
                type="text"
                className="mm-cw-search-input"
                placeholder="Search templates…"
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
              />
            </div>

            {replaceError && <div className="mm-error">{replaceError}</div>}

            <div className="mm-cw-templates">
              {templatesLoading && <div className="mm-empty">Loading templates…</div>}
              {!templatesLoading && filteredTemplates.length === 0 && (
                <div className="mm-empty">No templates found.</div>
              )}
              {filteredTemplates.map((t) => (
                <div
                  key={t.templateId}
                  className={`mm-cw-template-card${selectedTemplateId === t.templateId ? ' is-selected' : ''}`}
                  onClick={() => setSelectedTemplateId(t.templateId)}
                >
                  <div className="mm-cw-template-info">
                    <div className="mm-cw-template-name">{t.name}</div>
                    {t.category && <span className="mm-anchor-chip">{t.category}</span>}
                    {t.description && (
                      <div className="mm-cw-template-desc">{t.description}</div>
                    )}
                  </div>
                  {selectedTemplateId === t.templateId && (
                    <FontAwesomeIcon icon={faCheck} className="mm-cw-check" />
                  )}
                </div>
              ))}
            </div>

            <div className="mm-cw-footer">
              <div className="mm-cw-anchor-field">
                <label className="mm-cw-label" htmlFor="mm-anchor-date">
                  Anchor Date
                </label>
                <input
                  id="mm-anchor-date"
                  type="date"
                  className="mm-cw-date-input"
                  value={newAnchorDate}
                  onChange={(e) => setNewAnchorDate(e.target.value)}
                />
              </div>
              <button
                className="mm-cw-apply-btn"
                disabled={!selectedTemplateId || !newAnchorDate || replacing}
                onClick={handleReplaceWorkflow}
              >
                {replacing ? (
                  <>
                    <FontAwesomeIcon icon={faSync} spin /> Applying…
                  </>
                ) : (
                  'Apply Workflow'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatterMilestones;
